import React, { useState } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import {
  Avatar,
  Button,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import ScheduleIcon from '@material-ui/icons/Schedule';
import { formatDistanceToNowStrict, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useHistory } from 'react-router-dom';
import { Draggable } from 'react-beautiful-dnd';
import api from '../../services/api';
import { AEROS, stateText } from '../../styles/aerosTokens';

const avatarColorFor = id => AEROS.avatarCycle[id % AEROS.avatarCycle.length];
const initialsOf = (name = '') =>
  name.split(' ').filter(Boolean).slice(0, 2).map(p => p[0]).join('').toUpperCase();

const withAlpha = (hex, alpha) => {
  if (!hex || typeof hex !== 'string' || !hex.startsWith('#')) {
    return `rgba(124,124,124,${alpha})`;
  }
  const clean = hex.replace('#', '').slice(0, 6);
  const [r, g, b] = [0, 2, 4].map(i => parseInt(clean.slice(i, i + 2), 16));
  return `rgba(${r},${g},${b},${alpha})`;
};

const useStyles = makeStyles(theme => ({
  card: {
    padding: 14,
    background: theme.palette.aeros.surface2,
    border: `1px solid ${theme.palette.aeros.border}`,
    borderRadius: 12,
    boxShadow: 'none',
    marginBottom: 10,
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    transition: 'border-color 0.15s ease',
    '&:hover': {
      borderColor: 'rgba(160,68,255,0.4)',
    },
  },
  tagsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
  },
  tagChip: {
    padding: '1px 7px',
    borderRadius: 5,
    fontSize: 10,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  nameRow: {
    display: 'flex',
    alignItems: 'baseline',
    marginTop: 9,
    minWidth: 0,
  },
  cardTitle: {
    fontSize: 13.5,
    fontWeight: 600,
    color: theme.palette.text.primary,
    minWidth: 0,
  },
  ticketNumber: {
    fontSize: 11,
    fontWeight: 500,
    color: theme.palette.aeros.textTertiary,
    whiteSpace: 'nowrap',
    marginLeft: 6,
    flexShrink: 0,
  },
  cardDescription: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginTop: 3,
    lineHeight: 1.5,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  valueRow: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 6,
  },
  opportunityValue: {
    fontSize: 12,
    color: stateText(theme, theme.palette.success.main, 'success'),
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums',
    cursor: 'pointer',
  },
  assignValue: {
    fontSize: 11,
    color: theme.palette.aeros.textTertiary,
    fontWeight: 600,
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.text.secondary,
    },
  },
  removeValueButton: {
    padding: 0,
    marginLeft: theme.spacing(1),
    color: theme.palette.error.main,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 11,
  },
  avatar: {
    width: 24,
    height: 24,
    fontSize: 9,
    fontWeight: 800,
    color: '#07080D',
  },
  connectionTag: {
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.aeros.textTertiary,
    padding: '1px 6px',
    fontWeight: 700,
    borderRadius: 5,
    fontSize: 9,
    marginLeft: 8,
    maxWidth: 96,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  timeBox: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    flexShrink: 0,
  },
  lastMessageTime: {
    fontSize: 11,
    color: theme.palette.aeros.textTertiary,
    whiteSpace: 'nowrap',
  },
  lastMessageTimeUnread: {
    fontSize: 11,
    color: stateText(theme, theme.palette.success.main, 'success'),
    fontWeight: 700,
    whiteSpace: 'nowrap',
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 10,
    },
  },
  dialogPaper: {
    borderRadius: 10,
  },
  dialogButton: {
    borderRadius: 10,
  },
}));

const KanbanCard = ({ ticket, index, updateTicket }) => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();

  const [open, setOpen] = useState(false);
  const [newValue, setNewValue] = useState('');

  // O card inteiro navega para o ticket ("Ver ticket" do layout antigo).
  // Cliques logo apos um drag chegam com defaultPrevented=true (rbd).
  const handleCardClick = event => {
    if (event && event.defaultPrevented) return;
    history.push(`/tickets/${ticket.uuid}`);
  };

  const lastMessageTimeClass =
    Number(ticket.unreadMessages) > 0
      ? classes.lastMessageTimeUnread
      : classes.lastMessageTime;

  const customFields = ticket.contact.extraInfo || [];
  const valueFieldIndex = customFields.findIndex(field => field.name === 'valor');
  const valueField = valueFieldIndex !== -1 ? customFields[valueFieldIndex] : null;
  const opportunityValue = valueField ? parseFloat(valueField.value) : null;

  const handleOpenModal = event => {
    event.stopPropagation();
    setNewValue(valueField ? valueField.value.toString() : '');
    setOpen(true);
  };

  const handleCloseModal = () => {
    setOpen(false);
  };

  const updateContactValue = async (contactId, value) => {
    try {
      await api.put(`/contacts/${contactId}`, {
        extraInfo: [{ name: 'valor', value: value.toString() }],
      });
    } catch (error) {
      console.error('Erro ao atualizar o valor:', error);
    }
  };

  const removeContactValue = async event => {
    event.stopPropagation();
    try {
      await api.put(`/contacts/${ticket.contact.id}`, {
        extraInfo: [],
      });

      if (valueFieldIndex !== -1) {
        customFields.splice(valueFieldIndex, 1);
      }

      updateTicket({ ...ticket });

    } catch (error) {
      console.error('Erro ao remover o valor:', error);
    }
  };

  const handleSaveValue = async () => {
    await updateContactValue(ticket.contact.id, newValue);

    if (valueField) {
      valueField.value = newValue;
    } else {
      if (!ticket.contact.extraInfo) {
        ticket.contact.extraInfo = [];
      }
      ticket.contact.extraInfo.push({ name: 'valor', value: newValue });
    }

    updateTicket({ ...ticket });

    setOpen(false);
  };

  return (
    <Draggable draggableId={ticket.id.toString()} index={index}>
      {(provided, snapshot) => (
        <div
          className={classes.card}
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          onClick={handleCardClick}
          role="button"
          aria-label={`Ver ticket #${ticket.id}`}
        >
          {ticket.tags?.length > 0 && (
            <div className={classes.tagsRow}>
              {ticket.tags.map(tag => (
                <span
                  key={`kanban-card-tag-${ticket.id}-${tag.id}`}
                  className={classes.tagChip}
                  style={{
                    backgroundColor: withAlpha(tag.color || '#7c7c7c', 0.12),
                    color: stateText(theme, tag.color || '#7c7c7c'),
                  }}
                >
                  {tag.name}
                </span>
              ))}
            </div>
          )}
          <div
            className={classes.nameRow}
            style={ticket.tags?.length > 0 ? undefined : { marginTop: 0 }}
          >
            <Typography className={classes.cardTitle} noWrap>
              {ticket.contact.name || ' '}
            </Typography>
            <Typography className={classes.ticketNumber} component="span">
              #{ticket.id}
            </Typography>
          </div>
          <Tooltip title={ticket.lastMessage || ' '}>
            <Typography className={classes.cardDescription}>
              {ticket.lastMessage || ' '}
            </Typography>
          </Tooltip>
          <div className={classes.valueRow}>
            {opportunityValue !== null ? (
              <>
                <Typography
                  className={classes.opportunityValue}
                  onClick={handleOpenModal}
                >
                  R$ {opportunityValue.toFixed(2)}
                </Typography>
                <Tooltip title={'Remover'}>
                  <IconButton
                    className={classes.removeValueButton}
                    onClick={removeContactValue}
                    size="small"
                    aria-label="Remover valor"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <Typography
                className={classes.assignValue}
                onClick={handleOpenModal}
              >
                Atribuir valor
              </Typography>
            )}
          </div>
          <div className={classes.footer}>
            <Avatar
              src={ticket.contact.urlPicture}
              className={classes.avatar}
              style={{
                backgroundColor: ticket.contact.urlPicture
                  ? undefined
                  : avatarColorFor(ticket.contact.id || ticket.id || 0),
              }}
            >
              {!ticket.contact.urlPicture && initialsOf(ticket.contact.name)}
            </Avatar>
            {ticket.user && (
              <Typography className={classes.connectionTag}>
                {ticket.user.name.toUpperCase()}
              </Typography>
            )}
            <div className={classes.timeBox}>
              <ScheduleIcon
                className={lastMessageTimeClass}
                style={{ fontSize: 14 }}
              />
              <Typography className={lastMessageTimeClass}>
                {formatDistanceToNowStrict(parseISO(ticket.updatedAt), {
                  locale: ptBR,
                  addSuffix: true,
                })}
              </Typography>
            </div>
          </div>
          <Dialog
            open={open}
            onClose={handleCloseModal}
            onClick={event => event.stopPropagation()}
            classes={{ paper: classes.dialogPaper }}
          >
            <DialogTitle>{valueField ? 'Editar' : 'Atribuir'} Valor da Oportunidade</DialogTitle>
            <DialogContent>
              <TextField
                label="Valor"
                type="number"
                fullWidth
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                variant="outlined"
                size="small"
                className={classes.textField}
              />
            </DialogContent>
            <DialogActions>
              <Button
                onClick={handleCloseModal}
                color="secondary"
                variant="outlined"
                className={classes.dialogButton}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSaveValue}
                color="primary"
                variant="outlined"
                className={classes.dialogButton}
              >
                Salvar
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      )}
    </Draggable>
  );
};

export default KanbanCard;
